import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HiSearch, HiOutlineBell, HiChevronRight, HiChevronDown, HiMenu, HiX } from 'react-icons/hi';
import { useGeneral } from '../../../contexts';
import Button from '../../ui/Button/Button';
import NotificationsDropdown from '../../notifications/NotificationsDropdown';
import { useChannels } from '../../../hooks/useChannels';
import { generateCommunityUrl, generateEventAdminUrl, extractEventIdFromAdminUrl, extractEventPathFromAdminUrl, extractEventIdFromUrl, extractCommunitySlugFromUrl, createSlug, generateProfileUrl } from '../../../utils/urlSlug';
import Services from '../../../services/Services';
import { getPublicToken } from '../../../utils/publicToken';
import ChannelInitialIcon from '../../ui/ChannelInitialIcon/ChannelInitialIcon';
import SearchModal from '../../ui/SearchModal/SearchModal';
import styles from './Header.module.css';

type LanguageCode = 'en' | 'tr' | 'es' | 'de' | 'it' | 'fr';

const LANGUAGE_NAMES: Record<LanguageCode, string> = {
  en: 'English',
  tr: 'Türkçe',
  es: 'Español',
  de: 'Deutsch',
  it: 'Italiano',
  fr: 'Français'
};

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { auth, language } = useGeneral();
  const { isAuthenticated, user, logout, token } = auth;
  const { getTranslation, language: currentLanguage, changeLanguage } = language;

  // Check if we're in organizer panel route or admin channels selection page
  const isInAdminRoute = location.pathname.includes('/admin');
  const isOnAdminChannelsPage = location.pathname === '/communities/admin';
  const isOnCreateChannelPage = location.pathname === '/communities/create';
  
  // Check if we're in event editing route (has event path pattern)
  const isInEventRoute = useMemo(() => {
    if (!isInAdminRoute) return false;
    // Pattern: /communities/{channel-slug}/admin/{event-name}-{event-id}[/sub-route]
    const match = location.pathname.match(/\/communities\/[^/]+\/admin\/([^-]+-[^/]+)/);
    return match !== null;
  }, [isInAdminRoute, location.pathname]);

  const currentLang = currentLanguage as LanguageCode;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLanguageSubmenuOpen, setIsLanguageSubmenuOpen] = useState(false);
  const [isCommunitiesSubmenuOpen, setIsCommunitiesSubmenuOpen] = useState(false);
  const [isMobileCommunitiesExpanded, setIsMobileCommunitiesExpanded] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoLoading, setLogoLoading] = useState(true);
  const isMountedRef = useRef(true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const notificationsDropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Fetch user's channels
  const { channels: userChannels } = useChannels({
    token: auth.token || '',
    userID: user?.id || '',
    popular: false
  });

  // Filter admin channels
  const adminChannels = useMemo(() => {
    return userChannels.filter(channel => String(channel.isAdmin) === "1");
  }, [userChannels]);

  // Get current organization from URL if in organizer panel route
  const currentOrg = useMemo(() => {
    if (!isInAdminRoute) return null;
    const match = location.pathname.match(/\/communities\/([^/]+)\/admin/);
    if (!match) return null;
    const orgSlug = match[1];
    return userChannels.find((channel) => {
      const channelTitle = channel.channelTitle || channel.title;
      const channelSlug = channelTitle ? createSlug(channelTitle) : '';
      return channelSlug === orgSlug && String(channel.isAdmin) === "1";
    });
  }, [isInAdminRoute, location.pathname, userChannels]);

  // Only show organizer panel navbar if user is actually an admin of the channel or on admin channels page or create channel page
  const isOrganizerPanel = (isInAdminRoute && currentOrg !== null) || isOnAdminChannelsPage || isOnCreateChannelPage;

  // Check if we're on event detail page
  const isOnEventDetailPage = useMemo(() => {
    return location.pathname.startsWith('/events/') && !location.pathname.includes('/admin');
  }, [location.pathname]);

  // Check if we're on community detail page
  const isOnCommunityDetailPage = useMemo(() => {
    return location.pathname.startsWith('/communities/') && 
           !location.pathname.includes('/admin') && 
           location.pathname !== '/communities/admin' &&
           location.pathname !== '/communities/create' &&
           location.pathname !== '/communities';
  }, [location.pathname]);

  // Check if we're on events list page
  const isOnEventsPage = useMemo(() => {
    return location.pathname === '/events';
  }, [location.pathname]);

  // Check if we should show search button (hide on mobile events page)
  const [shouldShowSearchButton, setShouldShowSearchButton] = useState(true);
  
  useEffect(() => {
    const checkVisibility = () => {
      // Always show search button (events page uses global search modal now)
      setShouldShowSearchButton(true);
    };
    
    checkVisibility();
    window.addEventListener('resize', checkVisibility);
    return () => window.removeEventListener('resize', checkVisibility);
  }, [isOnEventsPage]);

  // Check for unread notifications on mount
  useEffect(() => {
    if (!isAuthenticated || !token || !user?.id) return;

    Services.GetUserNotifications(token)
      .then(response => {
        if (response.data.status && response.data.result) {
          const count = response.data.result.filter((n: any) => {
            const isRead = n.isRead === true || n.isRead === "1" || n.isRead === 1;
            return !isRead;
          }).length;
          setUnreadCount(count);
        }
      })
      .catch(() => {});
  }, [isAuthenticated, token, user?.id]);

  const handleUnreadCountChange = useCallback((count: number) => {
    setUnreadCount(count);
  }, []);

  // Handle organizer panel / create community button click
  const handleOrganizerPanelClick = async () => {
    // If on event detail page, navigate to event admin page
    if (isOnEventDetailPage) {
      const eventID = extractEventIdFromUrl(location.pathname);
      if (eventID) {
        try {
          // Get token
          let effectiveToken = auth.token;
          if (!effectiveToken) {
            effectiveToken = await getPublicToken();
          }

          if (effectiveToken) {
            // Fetch event details to get channel information
            const eventResponse = await Services.SocialHiveGetEventDetails(effectiveToken, eventID, new AbortController());
            
            if (eventResponse.data.status && eventResponse.data.result) {
              const event = eventResponse.data.result;
              const channelID = event.channelID;
              const channelTitle = event.channelTitle || event.channelDetails?.title;
              const eventTitle = event.eventTitle || event.title;

              // Find matching admin channel
              const matchingChannel = adminChannels.find(channel => 
                channel.channelID === channelID && String(channel.isAdmin) === "1"
              );

              if (matchingChannel && channelTitle && eventTitle) {
                const channelSlug = createSlug(channelTitle);
                const adminUrl = generateEventAdminUrl(eventTitle, eventID, channelSlug);
                navigate(adminUrl);
                return;
              }
            }
          }
        } catch (error) {
          // If fetching fails, fall through to default behavior
        }
      }
    }

    // If on community detail page, navigate to community admin page
    if (isOnCommunityDetailPage) {
      const communitySlug = extractCommunitySlugFromUrl(location.pathname);
      if (communitySlug) {
        // Find matching admin channel by slug
        const matchingChannel = adminChannels.find(channel => {
          const channelTitle = channel.channelTitle || channel.title;
          const channelSlug = channelTitle ? createSlug(channelTitle) : '';
          return channelSlug === communitySlug && String(channel.isAdmin) === "1";
        });

        if (matchingChannel) {
          const channelTitle = matchingChannel.channelTitle || matchingChannel.title;
          const channelSlug = channelTitle ? createSlug(channelTitle) : '';
          navigate(`/communities/${channelSlug}/admin`);
          return;
        }
      }
    }

    // Default behavior: route to selection page or create community
    if (adminChannels.length >= 1) {
      // User has admin channels - always route to selection page
      navigate('/communities/admin');
    } else {
      // No admin channels - route to create community
      navigate('/communities/create');
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (isMountedRef.current) {
        setIsScrolled(window.scrollY > 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Keyboard shortcut to open search modal (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyboardShortcut = (e: KeyboardEvent) => {
      // Check if Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isModifierPressed = isMac ? e.metaKey : e.ctrlKey;
      
      if (isModifierPressed && e.key === 'k' && !e.shiftKey && !e.altKey) {
        // Don't trigger if user is typing in an input, textarea, or contenteditable
        const target = e.target as HTMLElement;
        const isInputFocused = 
          target.tagName === 'INPUT' || 
          target.tagName === 'TEXTAREA' || 
          target.isContentEditable;
        
        if (!isInputFocused && isAuthenticated && !isOrganizerPanel) {
          e.preventDefault();
          setIsSearchModalOpen(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => {
      document.removeEventListener('keydown', handleKeyboardShortcut);
    };
  }, [isAuthenticated, isOrganizerPanel]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
        setIsLanguageSubmenuOpen(false);
      }
      
      // For notifications: only close if clicking outside the wrapper (which includes the button)
      // This allows the button to toggle the dropdown
      // Also check if click is outside the dropdown itself (for mobile overlay)
      if (notificationsRef.current && !notificationsRef.current.contains(target)) {
        // Check if click is inside the dropdown (for mobile overlay)
        const isInsideDropdown = notificationsDropdownRef.current?.contains(target);
        if (!isInsideDropdown) {
          setIsNotificationsOpen(false);
        }
      }
      
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(target)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isDropdownOpen || isNotificationsOpen || isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isNotificationsOpen, isMobileMenuOpen]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    logout();
  };

  const handleLanguageChange = (lang: LanguageCode) => {
    changeLanguage(lang);
    setIsLanguageSubmenuOpen(false);
    setIsCommunitiesSubmenuOpen(false);
    setIsMobileCommunitiesExpanded(false);
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setIsLanguageSubmenuOpen(false);
      setIsCommunitiesSubmenuOpen(false);
    }
  };

  const handleCommunityClick = (channel: typeof adminChannels[0]) => {
    const communityTitle = channel.channelTitle || channel.title;
    navigate(generateCommunityUrl(communityTitle, channel.channelID));
    setIsCommunitiesSubmenuOpen(false);
    setIsDropdownOpen(false);
  };

  const getInitials = () => {
    if (user?.name && user?.lastname) {
      return `${user.name.charAt(0)}${user.lastname.charAt(0)}`.toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const handleSearchClick = () => {
    setIsSearchModalOpen(true);
  };

  return (
    <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
      <div className={styles.container}>
        <div className={styles.leftSection}>
          <Link to="/" className={styles.logo}>
            {logoLoading && <div className="image-shimmer" style={{ position: 'absolute', width: '32px', height: '32px', borderRadius: '4px' }} />}
            <img 
              src="/lodos-favicon.png" 
              alt="Lodos" 
              className={styles.logoImage}
              onLoad={() => setLogoLoading(false)}
              onError={() => setLogoLoading(false)}
            />
            {isOrganizerPanel ? (
              <span className={styles.logoName}>
                ToGather <span className={styles.logoNameLight}>| {getTranslation('organizerPanel.title') || 'Organizatör Paneli'}</span>
              </span>
            ) : (
              <span className={styles.logoName}>ToGather</span>
            )}
          </Link>
          {isAuthenticated && !isOrganizerPanel && (
            <nav className={styles.nav}>
              <Link 
                to="/" 
                className={`${styles.navLink} ${isActive('/') ? styles.active : ''}`}
              >
                {getTranslation('navigation.discover')}
              </Link>
              <Link 
                to="/events" 
                className={`${styles.navLink} ${isActive('/events') ? styles.active : ''}`}
              >
                {getTranslation('navigation.events')}
              </Link>
              <Link 
                to="/communities" 
                className={`${styles.navLink} ${isActive('/communities') ? styles.active : ''}`}
              >
                {getTranslation('navigation.communities')}
              </Link>
            </nav>
          )}
        </div>

        <div className={styles.actions}>
          {isAuthenticated ? (
            <>
              {isOrganizerPanel && currentOrg ? (
                <Button 
                  variant="secondary" 
                  size="small" 
                  className={styles.backButton}
                  onClick={() => {
                    if (isInEventRoute) {
                      // Navigate to event detail page in new tab
                      const eventID = extractEventIdFromAdminUrl(location.pathname);
                      const eventPath = extractEventPathFromAdminUrl(location.pathname);
                      if (eventID && eventPath) {
                        // Event path is already in the format "event-slug-event-id"
                        // We can use it directly to construct the event URL
                        const eventUrl = `/events/${eventPath}`;
                        window.open(eventUrl, '_blank');
                      }
                    } else {
                      // Navigate to community detail page in new tab
                      const channelTitle = currentOrg.channelTitle || currentOrg.title;
                      if (channelTitle && currentOrg.channelID) {
                        navigate(generateCommunityUrl(channelTitle, currentOrg.channelID));
                      }
                    }
                  }}
                >
                  {isInEventRoute
                    ? (getTranslation('common.goToEvent') || 'Etkinliğe Git')
                    : (getTranslation('common.goToCommunity') || 'Topluluğa Git')
                  }
                </Button>
              ) : (
                <>
                  {isOnAdminChannelsPage ? (
                    <Button 
                      variant="secondary" 
                      size="small" 
                      className={styles.createButton}
                      onClick={() => navigate('/communities')}
                    >
                      {getTranslation('common.back') || 'Back'}
                    </Button>
                  ) : isOnCreateChannelPage ? (
                    <Button 
                      variant="secondary" 
                      size="small" 
                      className={styles.createButton}
                      onClick={() => navigate('/communities/admin')}
                    >
                      {getTranslation('common.back') || 'Back'}
                    </Button>
                  ) : (
                    <>
                      <Button 
                        variant="secondary" 
                        size="small" 
                        className={styles.createButton}
                        onClick={handleOrganizerPanelClick}
                      >
                        {adminChannels.length >= 1
                          ? (getTranslation('navigation.organizerPanel') || getTranslation('organizerPanel.title') || 'Organizatör Paneli')
                          : (getTranslation('navigation.createCommunity') || 'Create Community')
                        }
                      </Button>
                      {/* Hide search button on mobile events page */}
                      {shouldShowSearchButton && (
                        <button 
                          className={styles.searchButtonIcon} 
                          aria-label={getTranslation('common.search')}
                          onClick={handleSearchClick}
                        >
                          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                          {/* @ts-ignore - react-icons compatibility with React 19 */}
                          <HiSearch size={24} />
                        </button>
                      )}
                      <div className={styles.notificationsWrapper} ref={notificationsRef}>
                        <button
                          className={styles.iconButton}
                          aria-label={getTranslation('common.notifications')}
                          onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                        >
                          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                          {/* @ts-ignore - react-icons compatibility with React 19 */}
                          <HiOutlineBell size={24} />
                          {unreadCount > 0 && <span className={styles.notificationBadge} />}
                        </button>
                        {/* Desktop: Show dropdown in wrapper */}
                        {isNotificationsOpen && (
                          <div className={styles.desktopNotificationsDropdown}>
                            <NotificationsDropdown
                              onClose={() => setIsNotificationsOpen(false)}
                              dropdownRef={notificationsDropdownRef}
                              onUnreadCountChange={handleUnreadCountChange}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
              <div className={styles.avatarWrapper} ref={dropdownRef}>
                <button 
                  className={styles.iconButton} 
                  aria-label={getTranslation('common.profile')} 
                  onClick={toggleDropdown}
                >
                  <UserAvatar 
                    avatarUrl={user?.userAvatar || user?.avatar || user?.image || null}
                    initials={getInitials()} 
                  />
                </button>
                {isDropdownOpen && (
                  <div className={styles.dropdown}>
                    <button
                      className={styles.dropdownItem}
                      onClick={() => {
                        navigate(generateProfileUrl(user?.name, user?.lastname, user?.id || ''));
                        setIsDropdownOpen(false);
                      }}
                    >
                      {getTranslation('common.profile')}
                    </button>
                    <div className={styles.languageItemWrapper}>
                      <div 
                        className={styles.dropdownItem}
                        onClick={() => {
                          setIsLanguageSubmenuOpen(!isLanguageSubmenuOpen);
                          if (!isLanguageSubmenuOpen) {
                            setIsCommunitiesSubmenuOpen(false);
                          }
                        }}
                      >
                        <span>{getTranslation('common.language')}</span>
                        {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                        {/* @ts-ignore - react-icons compatibility with React 19 */}
                        <HiChevronRight size={16} />
                      </div>
                      {isLanguageSubmenuOpen && (
                        <div className={styles.languageSubmenu}>
                          {(Object.keys(LANGUAGE_NAMES) as LanguageCode[]).map((lang) => (
                            <button
                              key={lang}
                              className={`${styles.languageOption} ${currentLang === lang ? styles.languageOptionActive : ''}`}
                              onClick={() => handleLanguageChange(lang)}
                            >
                              {LANGUAGE_NAMES[lang]}
                              {currentLang === lang && <span className={styles.checkmark}>✓</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {adminChannels.length > 0 && (
                      <div className={styles.languageItemWrapper}>
                        <div 
                          className={styles.dropdownItem}
                          onClick={() => {
                            setIsCommunitiesSubmenuOpen(!isCommunitiesSubmenuOpen);
                            if (!isCommunitiesSubmenuOpen) {
                              setIsLanguageSubmenuOpen(false);
                            }
                          }}
                        >
                          <span>{getTranslation('common.myCommunities')}</span>
                          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                          {/* @ts-ignore - react-icons compatibility with React 19 */}
                          <HiChevronRight size={16} />
                        </div>
                        {isCommunitiesSubmenuOpen && (
                          <div className={styles.languageSubmenu}>
                            {adminChannels.map((channel) => {
                              const channelTitle = channel.channelTitle || channel.title;
                              const channelLogo = channel.channelLogo || channel.logo;
                              return (
                                <button
                                  key={channel.channelID}
                                  className={styles.communityOption}
                                  onClick={() => handleCommunityClick(channel)}
                                >
                                  <div className={styles.communityOptionContent}>
                                    {channelLogo ? (
                                      <img
                                        src={channelLogo}
                                        alt={channelTitle}
                                        className={styles.communityOptionLogo}
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <ChannelInitialIcon 
                                        channelName={channelTitle}
                                        className={styles.communityOptionLogo}
                                        size={24}
                                      />
                                    )}
                                    <span className={styles.communityOptionName}>{channelTitle}</span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    <button 
                      className={`${styles.dropdownItem} ${styles.logoutButton}`}
                      onClick={handleLogout}
                    >
                      {getTranslation('auth.logout')}
                    </button>
                  </div>
                )}
              </div>
              {/* Mobile Hamburger Menu */}
              <button 
                className={styles.mobileMenuButton}
                aria-label="Menu"
                onClick={() => {
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                  if (!isMobileMenuOpen) {
                    setIsMobileCommunitiesExpanded(false);
                  }
                }}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                {isMobileMenuOpen ? <HiX size={24} /> : <HiMenu size={24} />}
              </button>
            </>
          ) : (
            <>
              <Button 
                variant="secondary" 
                size="small" 
                className={styles.createButton}
                onClick={() => navigate('/register')}
              >
                {getTranslation('auth.register')}
              </Button>
              <Button 
                variant="primary" 
                size="small" 
                className={styles.createButton}
                onClick={() => navigate('/login')}
              >
                {getTranslation('auth.login')}
              </Button>
              {/* Mobile Hamburger Menu */}
              <button 
                className={styles.mobileMenuButton}
                aria-label="Menu"
                onClick={() => {
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                  if (!isMobileMenuOpen) {
                    setIsMobileCommunitiesExpanded(false);
                  }
                }}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                {/* @ts-ignore - react-icons compatibility with React 19 */}
                {isMobileMenuOpen ? <HiX size={24} /> : <HiMenu size={24} />}
              </button>
            </>
          )}
        </div>
      </div>
      
      {/* Mobile Notifications Dropdown - rendered at header level for proper positioning */}
      {isAuthenticated && isNotificationsOpen && (
        <div className={styles.mobileNotificationsOverlay}>
          <NotificationsDropdown
            onClose={() => setIsNotificationsOpen(false)}
            dropdownRef={notificationsDropdownRef}
            onUnreadCountChange={handleUnreadCountChange}
          />
        </div>
      )}
      
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className={styles.mobileMenu} ref={mobileMenuRef}>
          {isAuthenticated ? (
            <>
              {/* User Profile Section */}
              <div className={styles.mobileUserProfile}>
                <UserAvatar 
                  avatarUrl={user?.userAvatar || user?.avatar || user?.image || null}
                  initials={getInitials()} 
                />
                <div className={styles.mobileUserInfo}>
                  <div className={styles.mobileUserName}>
                    {user?.name && user?.lastname 
                      ? `${user.name} ${user.lastname}`
                      : user?.name || user?.email || 'User'
                    }
                  </div>
                  <div className={styles.mobileUserEmail}>
                    {user?.email || ''}
                  </div>
                </div>
              </div>
              
              <nav className={styles.mobileNav}>
                {adminChannels.length > 0 && (
                  <>
                    <div 
                      className={`${styles.mobileNavLink} ${isMobileCommunitiesExpanded ? styles.active : ''}`}
                      onClick={() => {
                        setIsMobileCommunitiesExpanded(!isMobileCommunitiesExpanded);
                        // Close language submenu if open (desktop)
                        if (!isMobileCommunitiesExpanded) {
                          setIsLanguageSubmenuOpen(false);
                          setIsCommunitiesSubmenuOpen(false);
                        }
                      }}
                    >
                      <span>{getTranslation('common.myCommunities')}</span>
                      {isMobileCommunitiesExpanded ? (
                        <>
                          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                          {/* @ts-ignore - react-icons compatibility with React 19 */}
                          <HiChevronDown 
                            size={16} 
                            className={styles.chevronIcon}
                          />
                        </>
                      ) : (
                        <>
                          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                          {/* @ts-ignore - react-icons compatibility with React 19 */}
                          <HiChevronRight 
                            size={16} 
                            className={styles.chevronIcon}
                          />
                        </>
                      )}
                    </div>
                    {isMobileCommunitiesExpanded && (
                      <div className={styles.mobileCommunitiesOptions}>
                        {adminChannels.map((channel) => {
                          const channelTitle = channel.channelTitle || channel.title;
                          const channelLogo = channel.channelLogo || channel.logo;
                          return (
                            <button
                              key={channel.channelID}
                              className={styles.mobileCommunityOption}
                              onClick={() => {
                                handleCommunityClick(channel);
                                setIsMobileMenuOpen(false);
                                setIsMobileCommunitiesExpanded(false);
                              }}
                            >
                              <div className={styles.mobileCommunityOptionContent}>
                                {channelLogo ? (
                                  <img
                                    src={channelLogo}
                                    alt={channelTitle}
                                    className={styles.mobileCommunityOptionLogo}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <ChannelInitialIcon 
                                    channelName={channelTitle}
                                    className={styles.mobileCommunityOptionLogo}
                                    size={24}
                                  />
                                )}
                                <span className={styles.mobileCommunityOptionName}>{channelTitle}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
                <Link
                  to={generateProfileUrl(user?.name, user?.lastname, user?.id || '')}
                  className={`${styles.mobileNavLink} ${location.pathname.startsWith('/profiles/') ? styles.active : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {getTranslation('common.profile')}
                </Link>
                <Link
                  to="/"
                  className={`${styles.mobileNavLink} ${isActive('/') ? styles.active : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {getTranslation('navigation.discover')}
                </Link>
                <Link 
                  to="/events" 
                  className={`${styles.mobileNavLink} ${isActive('/events') ? styles.active : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {getTranslation('navigation.events')}
                </Link>
                <Link 
                  to="/communities" 
                  className={`${styles.mobileNavLink} ${isActive('/communities') ? styles.active : ''}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {getTranslation('navigation.communities')}
                </Link>
              </nav>
              <div className={styles.mobileActions}>
                {isOnAdminChannelsPage ? (
                  <Button 
                    variant="secondary" 
                    size="small" 
                    className={styles.mobileCreateButton}
                    onClick={() => {
                      navigate('/communities');
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {getTranslation('common.back') || 'Back'}
                  </Button>
                ) : isOnCreateChannelPage ? (
                  <Button 
                    variant="secondary" 
                    size="small" 
                    className={styles.mobileCreateButton}
                    onClick={() => {
                      navigate('/communities/admin');
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {getTranslation('common.back') || 'Back'}
                  </Button>
                ) : (
                  <Button 
                    variant="secondary" 
                    size="small" 
                    className={styles.mobileCreateButton}
                    onClick={() => {
                      handleOrganizerPanelClick();
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {adminChannels.length >= 1
                      ? (getTranslation('navigation.organizerPanel') || getTranslation('organizerPanel.title') || 'Organizatör Paneli')
                      : (getTranslation('navigation.createCommunity') || 'Create Community')
                    }
                  </Button>
                )}
                <div className={styles.mobileLanguageSection}>
                  <div className={styles.mobileMenuItem}>
                    <span>{getTranslation('common.language')}</span>
                  </div>
                  <div className={styles.mobileLanguageOptions}>
                    {(Object.keys(LANGUAGE_NAMES) as LanguageCode[]).map((lang) => (
                      <button
                        key={lang}
                        className={`${styles.mobileLanguageOption} ${currentLang === lang ? styles.languageOptionActive : ''}`}
                        onClick={() => {
                          handleLanguageChange(lang);
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        {LANGUAGE_NAMES[lang]}
                        {currentLang === lang && <span className={styles.checkmark}>✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
                <button 
                  className={`${styles.mobileMenuItem} ${styles.mobileLogoutButton}`}
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                >
                  {getTranslation('auth.logout')}
                </button>
              </div>
            </>
          ) : (
            <div className={styles.mobileActions}>
              <Button 
                variant="secondary" 
                size="small" 
                className={styles.mobileCreateButton}
                onClick={() => {
                  navigate('/register');
                  setIsMobileMenuOpen(false);
                }}
              >
                {getTranslation('auth.register')}
              </Button>
              <Button 
                variant="primary" 
                size="small" 
                className={styles.mobileCreateButton}
                onClick={() => {
                  navigate('/login');
                  setIsMobileMenuOpen(false);
                }}
              >
                {getTranslation('auth.login')}
              </Button>
            </div>
          )}
        </div>
      )}
      
      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
        token={auth.token}
      />
    </header>
  );
};

const UserAvatar: React.FC<{ avatarUrl: string | null; initials: string }> = ({ avatarUrl, initials }) => {
  const [imageLoading, setImageLoading] = useState(true);
  
  return (
    <div className={styles.avatarContainer}>
      {avatarUrl ? (
        <>
          {imageLoading && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '50%' }} />}
          <img 
            src={avatarUrl} 
            alt="User avatar" 
            className={styles.avatarImage}
            onLoad={() => setImageLoading(false)}
            onError={(e) => {
              // Fallback to initials if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              setImageLoading(false);
              const initialsSpan = target.nextElementSibling as HTMLSpanElement;
              if (initialsSpan) {
                initialsSpan.style.display = 'flex';
              }
            }}
          />
        </>
      ) : null}
    <span 
      className={styles.avatarInitials}
      style={{ display: avatarUrl ? 'none' : 'flex' }}
    >
      {initials}
    </span>
  </div>
  );
};

export default Header;

